import SearchService from "services/search-service";
import WorkspaceService from "services/workspace-service";
import AiService from "services/ai-service";

/**
 * Fallback list if no own profiles or AI fails
 */
const fallbackProfilesList = [
    "BBC", "NASA", "Netflix", "Apple", "Google", "Microsoft", "Nike", "Adidas",
    "Tesla", "SpaceX", "Amazon", "Disney", "Meta", "Spotify", "Uber", "Airbnb"
];

/**
 * Utility: Select N random unique names.
 */
const getRandomProfiles = (arr, n = 9) => {
    const shuffled = [...arr].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, n);
};

/**
 * 🧠 Fetch suggested profiles using AI based on "Own Profiles".
 * Falls back to random trending profiles if needed.
 * 
 * @param {Function} onUpdate - callback to push incremental results to UI
 */
export const fetchSuggestedProfiles = async (onUpdate) => {
    try {
        console.log("🧠 Starting Suggested Profiles routine...");

        // 1. Fetch User's "Own Profiles"
        let aiKeywords = [];
        try {
            const workspaceProfilesPayload = await WorkspaceService.fetchWorkspaceProfiles();

            // Handle JSON:API structure (response.data) or simple array (response)
            // HttpService already unwraps response.data from Axios
            const workspaceProfiles = Array.isArray(workspaceProfilesPayload) ? workspaceProfilesPayload : (workspaceProfilesPayload.data || []);

            // Filter for OWN profiles (Handle flat or nested attributes)
            const ownProfiles = workspaceProfiles.filter(p => p.own === true || p.attributes?.own === true);

            // Schema has profile_id populated, so name is inside profile_id object
            const ownProfileNames = ownProfiles.map(p =>
                p.profile_id?.name ||
                p.profile_id?.attributes?.name ||
                p.attributes?.name ||
                p.name
            ).filter(Boolean); // Remote empty/undefined

            if (ownProfileNames.length > 0) {
                // 2. Ask AI for competitors
                aiKeywords = await AiService.getCompetitorSuggestions(ownProfileNames);
            }
        } catch (err) {
            console.warn("Failed to fetch own profiles for suggestions:", err);
        }

        // 3. Determine Final List of Keywords
        let targetKeywords = [];
        if (aiKeywords && aiKeywords.length > 0) {
            targetKeywords = aiKeywords;
        } else {
            console.log("🎲 Using fallback/trending profiles.");
            targetKeywords = getRandomProfiles(fallbackProfilesList, 9);
        }

        // 4. Start Searching (WebSocket Logic)
        // Reuse the logic from SearchTrendingProfiles but with our new targetKeywords
        const wsBaseUrl = process.env.REACT_APP_API_URL.replace(/^http/, "ws");
        const allPlatforms = ["Facebook", "YouTube", "Instagram"];

        const resultsMap = new Map(); // Map<`${platform}-${id}`, result>

        // Safety cap to avoid too many sockets if AI went crazy
        const keywordsToSearch = targetKeywords.slice(0, 9);

        for (let i = 0; i < keywordsToSearch.length; i++) {
            const name = keywordsToSearch[i];
            const platform = allPlatforms[i % allPlatforms.length]; // round-robin
            const clientId = `client-suggest-${platform}-${name.replace(/\s+/g, '')}-${Date.now()}`;

            try {
                const ws = new WebSocket(`${wsBaseUrl}/ws?clientId=${clientId}`);

                ws.onopen = async () => {
                    // console.log(`🔌 WS connected for suggestion: ${name} (${platform})`);
                    await SearchService.searchAllPlatforms(name, platform, false, clientId);
                };

                ws.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        if (data.type !== "search" || !Array.isArray(data.data)) return;

                        // Deduplicate
                        for (const item of data.data) {
                            const key = `${item.platform}-${item.id}`;
                            if (!resultsMap.has(key)) {
                                resultsMap.set(key, item);
                            }
                        }

                        // Push updates
                        const sortedResults = Array.from(resultsMap.values()).sort((a, b) => {
                            const countA = a.subscribersCount || a.followers_count || 0;
                            const countB = b.subscribersCount || b.followers_count || 0;
                            return countB - countA;
                        });
                        onUpdate(sortedResults);

                        if (data.status === "completed") {
                            ws.close();
                        }
                    } catch (err) {
                        // ignore
                    }
                };

                ws.onerror = () => ws.close();
            } catch (e) {
                console.error("WS setup error for suggestion", e);
            }
        }

    } catch (error) {
        console.error("Error in fetchSuggestedProfiles:", error);
        // Fallback?
    }
};
